// Cookie utility with security best practices
// Default expiration: 90 days (for authentication cookies)

// Use app-specific names so localhost:3000 (admin) and localhost:3001 (user)
// do not overwrite / clear each other's session (cookies ignore port).
export const ADMIN_AUTH_TOKEN_COOKIE = 'admin_auth_token';
export const LEGACY_AUTH_TOKEN_COOKIE = 'auth_token';

export const setCookie = (name, value, days = 90) => {
    if (value === undefined || value === null) return;

    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

    const encodedValue = encodeURIComponent(String(value));
    const isSecure = window.location.protocol === 'https:';

    const cookieString = isSecure
        ? `${name}=${encodedValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax; Secure`
        : `${name}=${encodedValue}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    document.cookie = cookieString;
};

export const getCookie = (name) => {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') c = c.substring(1, c.length);
        if (c.indexOf(nameEQ) === 0) {
            const raw = c.substring(nameEQ.length, c.length);
            try {
                return decodeURIComponent(raw);
            } catch {
                return raw;
            }
        }
    }
    return null;
};

export const removeCookie = (name) => {
    const isSecure = window.location.protocol === 'https:';
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=None; Secure`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
    if (isSecure) {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax; Secure`;
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict; Secure`;
    }
};

export const getAdminAuthToken = () =>
    getCookie(ADMIN_AUTH_TOKEN_COOKIE) || getCookie(LEGACY_AUTH_TOKEN_COOKIE);

export const setAdminAuthToken = (token, days = 90) => {
    setCookie(ADMIN_AUTH_TOKEN_COOKIE, token, days);
    // Remove legacy shared name so user app cannot collide with it on localhost
    removeCookie(LEGACY_AUTH_TOKEN_COOKIE);
};

export const clearAdminAuthToken = () => {
    removeCookie(ADMIN_AUTH_TOKEN_COOKIE);
    removeCookie(LEGACY_AUTH_TOKEN_COOKIE);
};

export const getAllCookies = () => {
    const cookies = {};
    document.cookie.split(';').forEach((cookie) => {
        const [name, ...rest] = cookie.trim().split('=');
        if (name) {
            const raw = rest.join('=');
            try {
                cookies[name] = decodeURIComponent(raw);
            } catch {
                cookies[name] = raw;
            }
        }
    });
    return cookies;
};

/** Backup admin session before leaving to user app (localStorage is port-scoped). */
export const backupAdminSession = () => {
    const payload = {
        admin_id: getCookie('admin_id'),
        company_id: getCookie('company_id'),
        auth_token: getAdminAuthToken(),
    };
    if (payload.admin_id) {
        localStorage.setItem('admin_session_backup', JSON.stringify(payload));
    }
};

/** Restore admin cookies if browser-back / shared-cookie wipe cleared them. */
export const restoreAdminSessionIfNeeded = () => {
    if (getCookie('admin_id') && getAdminAuthToken()) {
        return false;
    }

    const raw = localStorage.getItem('admin_session_backup');
    if (!raw) return false;

    try {
        const payload = JSON.parse(raw);
        if (payload.admin_id) setCookie('admin_id', payload.admin_id, 90);
        if (payload.company_id) setCookie('company_id', payload.company_id, 90);
        if (payload.auth_token) setAdminAuthToken(payload.auth_token, 90);
        return true;
    } catch {
        return false;
    }
};
